import { useState, useEffect } from "react";
import { useTheme } from "@mui/material/styles";
import {
  Box,
  Button,
  Grid,
  Paper,
  CircularProgress,
  Autocomplete,
  TextField,
  Checkbox,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";

import AssignmentIcon from "@mui/icons-material/Assignment";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";

import CardHeader from "../General/CardHeader";
import GlassCard from "../General/GlassCard";
import LegalFileStageFilter from "../General/LegalFileStageFilter";
import LegalFileTypeFilter from "../General/LegalFileTypeFilter";
import LegalFileStatusTaxFilter from "../General/LegalFileStatusTaxFilter";
import { downloadNotificationCertificateReport } from "../../services/NotificationCertificateService";
import { getLawyers } from "../../services/LawyerService";
import dayjs from "dayjs";
import { useToast } from "../../context/ToastContext";

export default function ReportNotificationCertificate() {
  const { showToast } = useToast();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const dateFilterOptions = [
    {
      id: "created",
      label: "Fecha de creación",
      startPayloadKey: "created_start_date",
      endPayloadKey: "created_end_date",
    },
    {
      id: "notification",
      label: "Fecha de notificación",
      startPayloadKey: "notification_start_date",
      endPayloadKey: "notification_end_date",
    },
    {
      id: "sent",
      label: "Fecha de envío",
      startPayloadKey: "sent_start_date",
      endPayloadKey: "sent_end_date",
    },
    {
      id: "finished",
      label: "Fecha de finalización",
      startPayloadKey: "finished_start_date",
      endPayloadKey: "finished_end_date",
    },
  ];

  const [dateFilterToAdd, setDateFilterToAdd] = useState("");
  const [activeDateFilters, setActiveDateFilters] = useState([]);

  const [dateRanges, setDateRanges] = useState({
    created: {
      start: null,
      end: dayjs(),
    },
    notification: {
      start: null,
      end: dayjs(),
    },
    sent: {
      start: null,
      end: dayjs(),
    },
    finished: {
      start: null,
      end: dayjs(),
    },
  });

  const [selectedStatus, setSelectedStatus] = useState([]);
  const [selectedResultStatus, setSelectedResultStatus] = useState([]);
  const [selectedColors, setSelectedColors] = useState([]);

  const [lawyers, setLawyers] = useState([]);
  const [selectedLawyers, setSelectedLawyers] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(false);

  const [selectedStages, setSelectedStages] = useState([]);
  const [selectedLegalFileStages, setSelectedLegalFileStages] = useState([]);
  const [selectedLegalFileTypes, setSelectedLegalFileTypes] = useState([]);
  const [selectedStatusTax, setSelectedStatusTax] = useState([]);
  const [selectedUploadedStatus, setSelectedUploadedStatus] = useState([]);

  const [loading, setLoading] = useState(false);

  const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
  const checkedIcon = <CheckBoxIcon fontSize="small" />;

  const statusOptions = [
    { id: "generated", label: "GENERADA" },
    { id: "sent", label: "ENVIADA" },
    { id: "finished", label: "FINALIZADA" },
    { id: "cancelled", label: "BAJA" },
  ];

  const resultStatusOptions = [
    { id: "success", label: "LOGRADO" },
    { id: "no_success", label: "NO LOGRADO" },
  ];

  const colorOptions = [
    { id: "yellow", label: "AMARILLO" },
    { id: "orange", label: "NARANJA" },
  ];

  const uploadedOptions = [
    { id: "yes", label: "SUBIDA" },
    { id: "no", label: "NO SUBIDA" },
  ];

  const stageOptions = [
    { id: "stage_1", label: "Etapa 1" },
    { id: "stage_2", label: "Etapa 2" },
    { id: "stage_3", label: "Etapa 3" },
    { id: "stage_4", label: "Etapa 4" },
  ];

  const availableDateFilterOptions = dateFilterOptions.filter(
    (option) => !activeDateFilters.includes(option.id),
  );

  useEffect(() => {
    async function fetchLawyers() {
      setLoadingOptions(true);

      try {
        const resLawyers = await getLawyers();
        setLawyers(resLawyers.data.data || []);
      } catch (error) {
        console.error("Error al cargar abogados:", error);
        showToast("Error al cargar abogados.", "error")
      } finally {
        setLoadingOptions(false);
      }
    }

    fetchLawyers();
  }, [showToast]);

  const handleAddDateFilter = () => {
    if (!dateFilterToAdd) return;

    setActiveDateFilters((prev) => {
      if (prev.includes(dateFilterToAdd)) {
        return prev;
      }

      return [...prev, dateFilterToAdd];
    });

    setDateFilterToAdd("");
  };

  const handleRemoveDateFilter = (filterId) => {
    setActiveDateFilters((prev) => prev.filter((id) => id !== filterId));

    setDateRanges((prev) => ({
      ...prev,
      [filterId]: {
        start: null,
        end: null,
      },
    }));
  };

  const handleChangeDateRange = (filterId, field, value) => {
    setDateRanges((prev) => ({
      ...prev,
      [filterId]: {
        ...prev[filterId],
        [field]: value,
      },
    }));
  };

  const validateDateFilters = () => {
    for (const filterId of activeDateFilters) {
      const option = dateFilterOptions.find((item) => item.id === filterId);
      const range = dateRanges[filterId];

      if (range?.start && !range?.end) {
        showToast(
          `Debe completar la fecha hasta de ${option.label.toLowerCase()}.`,
        "error")
        return false;
      }
      if (range?.start && range?.end && range.start.isAfter(range.end)) {
        showToast(
          `La ${option.label.toLowerCase()} desde no puede ser posterior a la fecha hasta.`,
        "error")
        return false;
      }
    }

    return true;
  };

  const appendDateFiltersToPayload = (payload) => {
    activeDateFilters.forEach((filterId) => {
      const option = dateFilterOptions.find((item) => item.id === filterId);
      const range = dateRanges[filterId];

      if (range?.start) {
        payload[option.startPayloadKey] = range.start.format("YYYY-MM-DD");
      }

      if (range?.end) {
        payload[option.endPayloadKey] = range.end.format("YYYY-MM-DD");
      }
    });
  };

  const handleGenerateReport = async () => {
    if (!validateDateFilters()) {
      return;
    }

    setLoading(true);
    try {
      const payload = {
        status: selectedStatus.map((s) => s.id),
        result_status: selectedResultStatus.map((s) => s.id),
        label_color: selectedColors.map((c) => c.id),
        lawyer_ids: selectedLawyers
          .filter((l) => l.id !== "select-all")
          .map((l) => l.id),
        stage_ids: selectedStages.map((s) => s.id),
        is_uploaded: selectedUploadedStatus.map((s) => s.id),
        legal_file_stage_ids: selectedLegalFileStages.map((s) => s.id),
        legal_file_types: selectedLegalFileTypes.map((t) => t.id),
        status_tax_ids: selectedStatusTax.map((s) => s.id),
      };

      appendDateFiltersToPayload(payload);

      const response = await downloadNotificationCertificateReport(payload);

      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;

      const now = new Date();
      const formattedDate = now
        .toISOString()
        .slice(0, 19)
        .replace(/:/g, "-")
        .replace("T", "_");

      a.download = `reporte_cedulas_notificacion_${formattedDate}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error(error);
      showToast("Ocurrió un error al generar el reporte.", "error")
    } finally {
      setLoading(false);
    }
  };

  return (
    <Grid container justifyContent="center">
      <Grid size={{ xs: 12, sm: 12, md: 12, lg: 10 }}>

        <GlassCard>
            <CardHeader
              icon={<AssignmentIcon />}
              title="Reportes de Cédulas de Notificación"
              description="Exporta las cédulas de notificación con filtros por fechas, estado, resultado, etiquetas, abogados y más."
            />

            <Grid container spacing={2}>
              <Grid size={{ xs: 12 }}>
                <Alert severity="info">
                  Las cédulas sin fecha de notificación también aparecen en el
                  reporte cuando no se filtra por fecha de notificación.
                </Alert>
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    gap: 2,
                    alignItems: { xs: "stretch", sm: "center" },
                  }}
                >
                  <FormControl fullWidth>
                    <InputLabel id="date-filter-select-label">
                      Agregar filtro de fecha
                    </InputLabel>

                    <Select
                      labelId="date-filter-select-label"
                      value={dateFilterToAdd}
                      label="Agregar filtro de fecha"
                      onChange={(event) =>
                        setDateFilterToAdd(event.target.value)
                      }
                      disabled={availableDateFilterOptions.length === 0}
                    >
                      {availableDateFilterOptions.map((option) => (
                        <MenuItem key={option.id} value={option.id}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleAddDateFilter}
                    disabled={!dateFilterToAdd}
                    sx={{
                      minWidth: { xs: "100%", sm: 180 },
                      py: 1.7,
                      borderRadius: 2,
                      fontWeight: 800,
                    }}
                  >
                    Agregar
                  </Button>
                </Box>

                {activeDateFilters.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Grid container spacing={2}>
                      {activeDateFilters.map((filterId) => {
                        const option = dateFilterOptions.find(
                          (item) => item.id === filterId,
                        );

                        const range = dateRanges[filterId];

                        return (
                          <Grid key={filterId} size={{ xs: 12 }}>
                            <Paper
                              elevation={0}
                              sx={{
                                p: 2,
                                borderRadius: 3,
                                background: isDark ? "rgba(255,255,255,0.04)" : "rgba(255,255,255,0.75)",
                                backdropFilter: "blur(10px)",
                                WebkitBackdropFilter: "blur(10px)",
                                border: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(0,0,0,0.08)",
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                  gap: 2,
                                  mb: 2,
                                }}
                              >
                                <Typography sx={{ fontWeight: 800 }}>
                                  {option.label}
                                </Typography>

                                <IconButton
                                  color="error"
                                  onClick={() =>
                                    handleRemoveDateFilter(filterId)
                                  }
                                >
                                  <DeleteOutlineIcon />
                                </IconButton>
                              </Box>

                              <Grid container spacing={2}>
                                <Grid size={{ xs: 12, sm: 6 }}>
                                  <DatePicker
                                    label="Desde"
                                    value={range.start}
                                    onChange={(newValue) =>
                                      handleChangeDateRange(
                                        filterId,
                                        "start",
                                        newValue,
                                      )
                                    }
                                    format="DD/MM/YYYY"
                                    slotProps={{
                                      textField: { fullWidth: true },
                                    }}
                                  />
                                </Grid>

                                <Grid size={{ xs: 12, sm: 6 }}>
                                  <DatePicker
                                    label="Hasta"
                                    value={range.end}
                                    onChange={(newValue) =>
                                      handleChangeDateRange(
                                        filterId,
                                        "end",
                                        newValue,
                                      )
                                    }
                                    format="DD/MM/YYYY"
                                    slotProps={{
                                      textField: { fullWidth: true },
                                    }}
                                  />
                                </Grid>
                              </Grid>
                            </Paper>
                          </Grid>
                        );
                      })}
                    </Grid>
                  </Box>
                )}
              </Grid>

              <Grid size={{ xs: 12, sm: 12, md: 12, lg: 6 }}>
                <Autocomplete
                  multiple
                  options={statusOptions}
                  disableCloseOnSelect
                  getOptionLabel={(option) => option.label}
                  isOptionEqualToValue={(option, value) =>
                    option.id === value.id
                  }
                  value={selectedStatus}
                  onChange={(event, newValue) => setSelectedStatus(newValue)}
                  renderOption={(props, option, { selected }) => (
                    <li {...props}>
                      <Checkbox
                        icon={icon}
                        checkedIcon={checkedIcon}
                        checked={selected}
                      />
                      {option.label}
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Estado"
                      placeholder="Seleccionar estado..."
                      fullWidth
                    />
                  )}
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 12, md: 12, lg: 6 }}>
                <Autocomplete
                  multiple
                  options={resultStatusOptions}
                  disableCloseOnSelect
                  getOptionLabel={(option) => option.label}
                  isOptionEqualToValue={(option, value) =>
                    option.id === value.id
                  }
                  value={selectedResultStatus}
                  onChange={(event, newValue) =>
                    setSelectedResultStatus(newValue)
                  }
                  renderOption={(props, option, { selected }) => (
                    <li {...props}>
                      <Checkbox
                        icon={icon}
                        checkedIcon={checkedIcon}
                        checked={selected}
                      />
                      {option.label}
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Resultado"
                      placeholder="Seleccionar resultado..."
                      fullWidth
                    />
                  )}
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 12, md: 12, lg: 6 }}>
                <Autocomplete
                  multiple
                  options={colorOptions}
                  disableCloseOnSelect
                  getOptionLabel={(option) => option.label}
                  isOptionEqualToValue={(option, value) =>
                    option.id === value.id
                  }
                  value={selectedColors}
                  onChange={(event, newValue) => setSelectedColors(newValue)}
                  renderOption={(props, option, { selected }) => (
                    <li {...props}>
                      <Checkbox
                        icon={icon}
                        checkedIcon={checkedIcon}
                        checked={selected}
                      />
                      {option.label}
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Color de etiqueta"
                      placeholder="Seleccionar color..."
                      fullWidth
                    />
                  )}
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 12, md: 12, lg: 6 }}>
                <Autocomplete
                  multiple
                  loading={loadingOptions}
                  options={[
                    {
                      id: "select-all",
                      name: "Seleccionar todos",
                      lastname: "",
                    },
                    ...lawyers,
                  ]}
                  disableCloseOnSelect
                  getOptionLabel={(option) =>
                    option.id === "select-all"
                      ? option.name
                      : `${option.name} ${option.lastname}` || option
                  }
                  isOptionEqualToValue={(option, value) =>
                    option.id === value.id
                  }
                  value={selectedLawyers}
                  onChange={(event, newValue) => {
                    const selectAllOption = newValue.find(
                      (val) => val.id === "select-all",
                    );

                    if (selectAllOption) {
                      if (selectedLawyers.length === lawyers.length) {
                        setSelectedLawyers([]);
                      } else {
                        setSelectedLawyers(lawyers);
                      }
                    } else {
                      setSelectedLawyers(newValue);
                    }
                  }}
                  renderOption={(props, option, { selected }) => {
                    const isSelectAll = option.id === "select-all";
                    const allSelected =
                      selectedLawyers.length === lawyers.length &&
                      lawyers.length > 0;

                    return (
                      <li {...props}>
                        <Checkbox
                          icon={icon}
                          checkedIcon={checkedIcon}
                          checked={isSelectAll ? allSelected : selected}
                        />
                        {isSelectAll
                          ? option.name
                          : `${option.name} ${option.lastname}` || option}
                      </li>
                    );
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Abogados"
                      placeholder="Seleccionar..."
                      fullWidth
                    />
                  )}
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 12, md: 12, lg: 6 }}>
                <Autocomplete
                  multiple
                  options={stageOptions}
                  disableCloseOnSelect
                  getOptionLabel={(option) => option.label}
                  isOptionEqualToValue={(option, value) =>
                    option.id === value.id
                  }
                  value={selectedStages}
                  onChange={(event, newValue) => setSelectedStages(newValue)}
                  renderOption={(props, option, { selected }) => (
                    <li {...props}>
                      <Checkbox
                        icon={icon}
                        checkedIcon={checkedIcon}
                        checked={selected}
                      />
                      {option.label}
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Etapas de la cédula"
                      placeholder="Seleccionar etapa..."
                      fullWidth
                    />
                  )}
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 12, md: 12, lg: 6 }}>
                <LegalFileStageFilter
                  value={selectedLegalFileStages}
                  onChange={setSelectedLegalFileStages}
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 12, md: 12, lg: 6 }}>
                <LegalFileTypeFilter
                  value={selectedLegalFileTypes}
                  onChange={setSelectedLegalFileTypes}
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 12, md: 12, lg: 6 }}>
                <LegalFileStatusTaxFilter
                  value={selectedStatusTax}
                  onChange={setSelectedStatusTax}
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 12, md: 12, lg: 6 }}>
                <Autocomplete
                  multiple
                  options={uploadedOptions}
                  disableCloseOnSelect
                  getOptionLabel={(option) => option.label}
                  isOptionEqualToValue={(option, value) =>
                    option.id === value.id
                  }
                  value={selectedUploadedStatus}
                  onChange={(event, newValue) =>
                    setSelectedUploadedStatus(newValue)
                  }
                  renderOption={(props, option, { selected }) => (
                    <li {...props}>
                      <Checkbox
                        icon={icon}
                        checkedIcon={checkedIcon}
                        checked={selected}
                      />
                      {option.label}
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Carga de Cédula (Escritos)"
                      placeholder="Seleccionar si fue subida..."
                      fullWidth
                    />
                  )}
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  onClick={handleGenerateReport}
                  sx={{
                    borderRadius: 2,
                    fontWeight: 800,
                  }}
                  disabled={loading}
                >
                  {loading ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    "Generar Reporte de Cédulas"
                  )}
                </Button>
              </Grid>
            </Grid>
        </GlassCard>
      </Grid>
    </Grid>
  );
}
